import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { formatMinutes, QUIZ_LENGTH_MS, QuizBoard, Ranked } from './quizModel';
import { GuessTiles, QUIZ_COLORS } from './Tiles';

/**
 * The guess line: the same 96px field as the basic race graph. While the quiz
 * runs, each guess sits at the minute it was locked (above or below the line,
 * whichever is free), on an axis that runs from the opening to a little past
 * now: it zooms out as the clock runs, so early guesses start mid-line and
 * drift left. The axis is square-root, so the first minutes keep room late in
 * the game instead of all piling up at the start. When it closes, the same
 * markers slide into rank order and their tiles take the score colours, so the
 * leaderboard never needs more room than the race did.
 */

const FIELD_H = 96;
const AXIS_Y = 46;
const PAD = 3; // % at each end of the axis
/** The open axis never shows less than this much time... */
const MIN_SPAN_MS = 5 * 60_000;
/** ...and runs this far past now, so the newest guess isn't on the edge. */
const HEADROOM = 1.25;

/** How much time the open axis shows, `elapsedMs` into the quiz. */
export function visibleSpanMs(elapsedMs: number): number {
  return Math.min(QUIZ_LENGTH_MS, Math.max(MIN_SPAN_MS, elapsedMs * HEADROOM));
}

/** Where `ms` sits on an open axis showing `spanMs`, 0 to 1 (square-root). */
export function axisFraction(ms: number, spanMs: number): number {
  return Math.sqrt(Math.min(Math.max(ms, 0), spanMs) / spanMs);
}

const Field = styled.div`
  position: relative;
  height: ${FIELD_H}px;
  width: 100%;
`;

const Axis = styled.div`
  position: absolute;
  left: ${PAD}%;
  right: ${PAD}%;
  top: ${AXIS_Y}px;
  height: 1px;
  background: ${QUIZ_COLORS.line};
`;

const End = styled.span`
  position: absolute;
  top: ${AXIS_Y + 4}px;
  font-size: 0.6rem;
  font-style: italic;
  color: #c3c7cf;
  transition: opacity 0.2s;
`;

const Marker = styled(motion.div)`
  position: absolute;
  top: 0;
  height: ${FIELD_H}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`;

const Name = styled.span<{ $strong?: boolean }>`
  font-size: 0.62rem;
  line-height: 1.15;
  white-space: nowrap;
  color: ${(p) => (p.$strong ? 'rgb(35, 35, 35)' : QUIZ_COLORS.ink2)};
  font-weight: ${(p) => (p.$strong ? 600 : 400)};
`;

const Meta = styled.span`
  font-size: 0.58rem;
  line-height: 1.15;
  white-space: nowrap;
  color: ${QUIZ_COLORS.label};
`;

const Dot = styled.span<{ $color: string; $ring?: string }>`
  position: absolute;
  top: ${AXIS_Y - 3}px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: ${(p) => (p.$ring ? `0 0 0 2px ${p.$ring}` : 'none')};
`;

const Now = styled.div`
  position: absolute;
  top: ${AXIS_Y - 10}px;
  /* stops at the axis, clear of the end labels under it */
  height: 13px;
  width: 1px;
  background: ${QUIZ_COLORS.brick};
  opacity: 0.45;
`;

interface GuessLineProps {
  board: QuizBoard;
  guesses: Ranked[];
  elapsedMs: number;
  closed: boolean;
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(340);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth || 340);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setW(el.clientWidth || 340));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const keyOf = (g: Ranked) => g.id;

const GuessLine: FC<GuessLineProps> = ({ board, guesses, elapsedMs, closed }) => {
  const [ref, width] = useWidth<HTMLDivElement>();
  const reduce = useReducedMotion();
  const wide = width > 600;
  const tile = board.rows.length > 1 ? (wide ? 12 : 9) : wide ? 16 : 13;
  const markerPx = Math.max(tile * 4 + 10, 64);
  const markerPct = (markerPx / width) * 100;
  const half = Math.min(25, markerPct / 2);
  const span = 100 - PAD * 2;
  const spanMs = visibleSpanMs(elapsedMs);
  const clampX = (x: number) => Math.min(100 - half, Math.max(half, x));

  // closed: ranked players left to right, the author last
  const order = closed
    ? [...guesses.filter((g) => !g.isAuthor && !g.superseded), ...guesses.filter((g) => g.superseded), ...guesses.filter((g) => g.isAuthor)]
    : [];
  const xOf = (g: Ranked): number => {
    if (!closed) return clampX(PAD + axisFraction(g.atMs, spanMs) * span);
    const i = order.indexOf(g);
    return half + i * ((100 - 2 * half) / Math.max(order.length - 1, 1));
  };

  // above or below the line, whichever is free; once closed, ranks alternate
  const rowOf = new Map<string, number>();
  const lastX = [-Infinity, -Infinity];
  const sorted = [...guesses].sort((a, b) => xOf(a) - xOf(b));
  sorted.forEach((g, i) => {
    const x = xOf(g);
    const row = closed
      ? i % 2
      : x - lastX[0] >= markerPct
        ? 0
        : x - lastX[1] >= markerPct
          ? 1
          : lastX[0] <= lastX[1]
            ? 0
            : 1;
    lastX[row] = x;
    rowOf.set(keyOf(g), row);
  });

  // The dot stays at the true minute; the label beside it is nudged sideways
  // until it clears its neighbours in the same row, with a thin line back.
  const labelX = new Map<string, number>();
  [0, 1].forEach((row) => {
    const inRow = sorted.filter((g) => rowOf.get(keyOf(g)) === row);
    const pos = inRow.map((g) => xOf(g));
    for (let i = 1; i < pos.length; i++) pos[i] = Math.max(pos[i], pos[i - 1] + markerPct);
    for (let i = pos.length - 1; i >= 0; i--) {
      pos[i] = Math.min(pos[i], 100 - half, i < pos.length - 1 ? pos[i + 1] - markerPct : Infinity);
    }
    inRow.forEach((g, i) => labelX.set(keyOf(g), Math.max(half, pos[i])));
  });

  // a guess drawn under the line near an end sits on that end's label: hide the label
  const lowerX = guesses.filter((g) => rowOf.get(keyOf(g)) === 1).map((g) => labelX.get(keyOf(g)) ?? xOf(g));
  const startHidden = lowerX.some((x) => x - half < PAD + (12 / width) * 100);
  const endHidden = lowerX.some((x) => x + half > 100 - PAD - (26 / width) * 100);

  // open: the axis zooms out every second, so markers glide at the clock's pace
  const ease = closed
    ? { duration: reduce ? 0 : 0.6, ease: 'easeInOut' as const }
    : { duration: reduce ? 0 : 1, ease: 'linear' as const };
  const TOP_END = 37; // bottom of an upper label
  const BOTTOM_START = AXIS_Y + 9; // top of a lower label

  return (
    <Field ref={ref} aria-label={closed ? 'final ranking' : 'guesses so far'}>
      <Axis />
      {!closed && (
        <>
          <End style={{ left: `${PAD}%`, opacity: startHidden ? 0 : 1 }}>0</End>
          <End style={{ right: `${PAD}%`, opacity: endHidden ? 0 : 1 }}>{Math.round(spanMs / 60_000)}m</End>
          <Now style={{ left: `${PAD + axisFraction(elapsedMs, spanMs) * span}%` }} />
        </>
      )}
      <svg width="100%" height={FIELD_H} style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }} aria-hidden="true">
        {guesses.map((g) => {
          const below = rowOf.get(keyOf(g)) === 1;
          const dx = `${xOf(g)}%`;
          const lx = `${labelX.get(keyOf(g)) ?? xOf(g)}%`;
          return (
            <motion.line
              key={keyOf(g)}
              initial={false}
              animate={{ x1: dx, x2: lx, opacity: g.superseded ? 0.4 : 1 }}
              transition={ease}
              y1={AXIS_Y}
              y2={below ? BOTTOM_START : TOP_END}
              stroke={QUIZ_COLORS.line}
              strokeWidth={1}
            />
          );
        })}
      </svg>
      <AnimatePresence initial={false}>
        {guesses.map((g) => {
          const below = rowOf.get(keyOf(g)) === 1;
          const x = xOf(g);
          const lx = labelX.get(keyOf(g)) ?? x;
          const meta = closed
            ? g.superseded
              ? `not counted · ${g.score ?? 0}`
              : g.isAuthor
                ? 'author'
                : `#${g.rank ?? '?'} · ${g.score ?? 0} pts`
            : g.attemptOf > 1
              ? `${formatMinutes(g.atMs)} · ${g.attempt} of 2`
              : formatMinutes(g.atMs);
          return (
            <React.Fragment key={keyOf(g)}>
              <Marker
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6, left: `${lx}%` }}
                animate={{ opacity: g.superseded ? 0.4 : 1, scale: 1, left: `${lx}%` }}
                exit={{ opacity: 0 }}
                transition={ease}
                style={{ x: '-50%', zIndex: g.isYou ? 2 : 1, top: below ? BOTTOM_START : 0, height: 'auto' }}
              >
                <GuessTiles rows={board.rows} picks={g.picks} marks={closed ? g.marks : undefined} size={tile} />
                <Name $strong={g.isYou || (closed && g.rank === 1)}>{g.isYou ? 'you' : g.username}</Name>
                <Meta>{meta}</Meta>
              </Marker>
              <Dot
                as={motion.span}
                initial={false}
                animate={{ left: `${x}%`, opacity: g.superseded ? 0.4 : 1 }}
                transition={ease}
                style={{ x: '-50%' }}
                $color={g.isAuthor ? QUIZ_COLORS.yellow : g.isYou ? QUIZ_COLORS.brick : '#9ca3af'}
                $ring={g.isAuthor ? QUIZ_COLORS.yellowWash : undefined}
              />
            </React.Fragment>
          );
        })}
      </AnimatePresence>
    </Field>
  );
};

export default GuessLine;
